<template>
  <div class="dcb-faq">
    <Heading
      tag="h2"
      align="center"
      class="dcb-teaser__title"
      size="md"
      spaces-bottom
    >
      Häufig gestellte Fragen
    </Heading>
    <!-- We have Components, so we can't make it dynamic! -->
    <AccordionItem
      v-if="!props.simple"
      @update:isOpen="
        (args) =>
          accordionToggle(args, slugify('Wie richte ich „Zahl einfach per Handyrechnung“ ein?'))
      "
    >
      <template #title>
        <h3>Wie richte ich „Zahl einfach per Handyrechnung“ ein?</h3>
      </template>
      <template #content>
        <AccordionItem @update:isOpen="(args) => accordionToggle(args, slugify('Apple/iOS'))">
          <template #title>
            <Paragraph> Apple/iOS </Paragraph>
          </template>
          <template #content>
            <ul class="list">
              <li class="list__element--numbered">
                QR-Code scannen oder Link klicken, um zu den Einstellungen Ihres Smartphones zu
                gelangen.
                <QrCodeAppStore />
              </li>
              <li class="list__element--numbered">Auf „Zahlungsmethode hinzufügen“ klicken.</li>
              <li class="list__element--numbered">Die Zahlungsmethode „Handyrechnung“ auswählen</li>
            </ul>
          </template>
        </AccordionItem>
        <AccordionItem
          @update:isOpen="
            (args) => accordionToggle(args, slugify('Manuelle Einrichtung für Apple/iOS'))
          "
        >
          <template #title>
            <Paragraph> Manuelle Einrichtung für Apple/iOS </Paragraph>
          </template>
          <template #content>
            <StepsAppleManual />
          </template>
        </AccordionItem>
        <AccordionItem @update:isOpen="(args) => accordionToggle(args, slugify('Android/Google'))">
          <template #title>
            <Paragraph> Android/Google </Paragraph>
          </template>
          <template #content>
            <ul class="list">
              <li class="list__element--numbered">
                QR-Code scannen oder Link klicken, um zu den Einstellungen Ihres Smartphones zu
                gelangen.
                <QrCodeGooglePlay />
              </li>
              <li class="list__element--numbered">
                Die Zahlungsmethode „Telekom - Zahl einfach per Handyrechnung“ auswählen.
              </li>
            </ul>
          </template>
        </AccordionItem>
        <AccordionItem
          @update:isOpen="
            (args) => accordionToggle(args, slugify('Manuelle Einrichtung für Android/Google'))
          "
        >
          <template #title>
            <Paragraph> Manuelle Einrichtung für Android/Google </Paragraph>
          </template>
          <template #content>
            <StepsGoogleManual />
          </template>
        </AccordionItem>
      </template>
    </AccordionItem>

    <AccordionItem
      v-for="(faq, key) in faqs"
      :key="key"
      @update:isOpen="(args) => accordionToggle(args, slugify(faq.title))"
    >
      <template #title>
        <h3>{{ faq.title }}</h3>
      </template>
      <template #content>
        <div v-html="faq.content" />
      </template>
    </AccordionItem>
  </div>
</template>

<script setup lang="ts">
import { AccordionItem, Paragraph, Heading, slugify } from '@i22-td-smarthome/component-library';
import StepsAppleManual from '@/components/dcb/steps-apple-manual.vue';
import StepsGoogleManual from '@/components/dcb/steps-google-manual.vue';
import QrCodeGooglePlay from '@/components/dcb/qr-code-google-play.vue';
import QrCodeAppStore from '@/components/dcb/qr-code-app-store.vue';
import Tealium from '~/lib/tealium';

const props = defineProps({
  simple: { type: Boolean, default: false },
  shortVersion: { type: Boolean, default: false },
  withLottery: { type: Boolean, default: false },
});

let faqs = [
  {
    title:
      'Muss ich mich für das Bezahlen per Handyrechnung registrieren oder Bankdaten hinterlegen?',
    content:
      'Nein, das ist nicht erforderlich. Die Abrechnung erfolgt über Ihre monatliche Mobilfunkrechnung bzw. Ihr Prepaid-Guthaben.',
  },
  {
    title: 'Wie erfolgt die Abrechnung bei „Zahl einfach per Handyrechnung“?',
    content:
      'Bei Prepaid-Kunden erfolgt eine sofortige Abbuchung des Betrags vom Guthaben. Vertragskunden zahlen mit ihrer nächsten monatlichen Handyrechnung.',
  },
  {
    title: 'Welche Produkte können mit „Zahl einfach per Handyrechnung“ gekauft werden?',
    content:
      'Fast alles, was digital zu erwerben ist. Es können zum Beispiel Apps, Spiele, Musik, Filme, E-Books und Tickets per Handyrechnung bezahlt werden. Auch Fahrkarten oder Parkscheine lassen sich auf diese Weise einfach, sicher und schnell kaufen. Unsere Partner finden Sie <a href="/zahlung-per-handyrechnung#partner" class="link">hier</a>.',
  },
  {
    title: 'Was ist eine Drittanbietersperre und wie kann ich meine Drittanbietersperre aufheben? ',
    content: `Über eine Drittanbietersperre ist die Abrechnung aller Drittanbieter-Dienste (z. B. App-Stores, Fahrkarten, Parkscheine,…) über die Mobilfunkrechnung gesperrt. Sie können Drittanbieter-Dienste über Ihren Mobilfunk-Vertrag sperren und entsperren. Übrigens können auch nur einzelne Drittanbieter (z. B. nur der AppStore) entsperrt werden. Dazu gehen Sie bitte wie folgt vor:
    <ul class="list">
    <li class="list__element--numbered">Loggen Sie sich im <a href="https://cap.telekom.de/trinityspace/ui/auth?module=trinityspace&page=barringprofile" target="_blank" rel="noopener" class="link link--external">Kundencenter <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span></a> ein.</li>
    <li class="list__element--numbered">Die Sperrkonfiguration öffnet sich und Sie können über den Button "Bearbeiten" die Drittanbieter sperren oder entsperren.</li>
</ul>`,
  },
  {
    title: 'Mein Kauf kann nicht abgewickelt werden. Was muss ich tun?',
    content: `Dies kann zwei Gründe haben:
    <ul class="list">
    <li class="list__element--numbered">Das Guthaben auf der Prepaid-Karte ist zu gering: In diesem Fall lade Dein Prepaid Guthaben auf und wiederhole den Kaufvorgang. </li>
    <li class="list__element--numbered">Die SIM-Karte ist für das Bezahlen per Handyrechnung durch eine Drittanbietersperre gesperrt: Du kannst Drittanbieter-Dienste für Deinen Mobilfunk-Vertrag sperren und entsperren. Dazu gehe bitte wie folgt vor:
      <ul>
      <li class="list__element--alpha">Logge Dich im <a href="https://www.telekom.de/mein-kundencenter" target="_blank" class="link link--external">Kundencenter <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span></a> ein.</li>
<li class="list__element--alpha">Klicke auf "Vertragsdetails" bei dem Mobilfunk-Vertrag, bei dem Du die Dienste verwalten möchtest.</li>
<li class="list__element--alpha">Scrolle nach unten bis "Meine Rufnummer" und klicke dort auf den Button "Drittanbietersperren verwalten".</li>
<li class="list__element--alpha">Der Sperrkonfigurator öffnet sich und Du kannst über den Button "Bearbeiten" die Drittanbieter sperren oder entsperren.</li>
<li class="list__element--alpha">Wiederhole den Kaufvorgang.</li>
</li>
</ul>
    </li>
</ul>`,
  },
  {
    title:
      'Wie kann ich „Zahl einfach per Handyrechnung“ als primäres Zahlverfahren bei mir im Smartphone hinterlegen?',
    content:
      'Du hast „Zahl einfach per Handyrechnung“ als Zahlungsmethode eingerichtet, aber dennoch ist es nicht das primäre Zahlverfahren (erkennbar daran, dass es nicht oben als erste Zahlmethode aufgelistet ist). Über „Bearbeiten“ (iOS) bzw. „Verwalten“ (Android) kannst Du die Reihenfolge der Zahlungsmethoden ändern und „Zahl einfach per Handyrechnung“ nach ganz oben setzen, um es als bevorzugtes Zahlungsmittel einzustellen.',
  },
];

if (props.shortVersion) {
  faqs = faqs.slice(0, 4);
}

if (props.withLottery) {
  faqs.push({
    title: 'Wann und wie erhalte ich den Gutschein?',
    content:
      'Nachdem Sie im Aktionszeitraum den ersten Kauf mit „Zahl einfach per Handyrechnung“ getätigt haben, senden wir Ihnen innerhalb von 30 Tagen den Zugangscode für den Amazon.de-Gutschein per E-Mail (an die bei der Registrierung angegebene E-Mail-Adresse) zu',
  });
}

const accordionToggle = (isOpen, title) => {
  Tealium.link(`content.button.faq.${isOpen ? 'open' : 'close'}.${title}`);
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-faq {
  margin-bottom: $grid-base * 5;
}
</style>
